import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  BOMComponentsWithSageInfoResult,
  computeBundleInfo,
  currentBomHeader,
  getEnabledEntityRoutes,
  isKeptOutOfSage,
  loadErrorMessageFor,
  OverviewBundle,
  OverviewEntityResult,
  PagedResult,
  ReconciliationResult,
  ResolveUomMappingRequest,
  SageCompany,
  SageConflictLog,
  SageConnectionStatus,
  SageEntityTypeName,
  SageItemAdjustment,
  SageItemAdjustmentReason,
  SageConfirmResult,
  SageConfirmSelectedResult,
  SagePendingReviewResult,
  SageReconciliationMatch,
  SageReconciliationSession,
  SageSyncError,
  SageSyncErrorPage,
  SageSyncErrorRow,
  SageActionableErrorPage,
  SageLatestSessionErrorPage,
  SageSyncLog,
  SageSyncProvenance,
  StockAdjustmentFilter,
  SyncStatusResponse,
  TenantSageSettings,
  ItemReportGroup,
  UpdateMatchDecisionRequest,
} from './sage-integration.model';
import {
  FixMatchType,
  SageLinkAddDetails,
  SageLinkAddRequest,
  SageLinkMoveDetails,
  SageLinkMoveRequest,
  SageLinkRow,
} from './sage-fix-matches.model';
import { sellsAsIs } from '../models/item-type';

export interface SyncLogFilter {
  entityType?: SageEntityTypeName;
  direction?: 'Outbound' | 'Inbound';
  status?: 'Running' | 'Succeeded' | 'PartialFailure' | 'Failed';
  page?: number;
  pageSize?: number;
}

export interface SyncErrorFilter {
  entityType?: SageEntityTypeName;
  direction?: 'Outbound' | 'Inbound';
  recordSource?: 'Aivor' | 'Sage';
  category?: string[];
  syncLogId?: number;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  /** Without it the endpoint lists unresolved problems only, one per record and reason. */
  includeResolved?: boolean;
}

export interface ActionableErrorFilter {
  entityType?: SageEntityTypeName;
  search?: string;
  /** Bounds the occurrence/first-seen rollup only, never which problems are listed. */
  since?: string;
  page?: number;
  pageSize?: number;
}

export interface ConflictLogFilter {
  entityType?: SageEntityTypeName;
  /** Matches the Aivor record id or the record's resolved display name. */
  search?: string;
  page?: number;
  pageSize?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SageIntegrationService {
  /** Sum of "needs review" rows across every entity, kept up to date by whichever screen last loaded matches. */
  private reviewCountSubject = new BehaviorSubject<number>(0);
  reviewCount$ = this.reviewCountSubject.asObservable();

  /** Cached status/onboarding-state/entity-session fetch for the overview screen — populated
   * during the connect screen's analysing animation so navigating to overview doesn't repeat it. */
  private overviewBundle$: Observable<OverviewBundle> | null = null;

  constructor(private http: HttpClient) {}

  setReviewCount(count: number): void {
    this.reviewCountSubject.next(count);
  }

  private authHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  connect(): Observable<{ authorizeUrl: string }> {
    return this.http.get<{ authorizeUrl: string }>(`${environment.apiUrl}/api/Sage/connect`, {
      headers: this.authHeaders(),
    });
  }

  getStatus(): Observable<SageConnectionStatus> {
    return this.http.get<SageConnectionStatus>(`${environment.apiUrl}/api/Sage/status`, {
      headers: this.authHeaders(),
    });
  }

  getCompanies(): Observable<SageCompany[]> {
    return this.http.get<SageCompany[]>(`${environment.apiUrl}/api/Sage/companies`, {
      headers: this.authHeaders(),
    });
  }

  selectCompany(sageCompanyId: string, sageCompanyName?: string): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/api/Sage/company`,
      { sageCompanyId, sageCompanyName },
      { headers: this.authHeaders() }
    );
  }

  disconnect(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/api/Sage/disconnect`, null, {
      headers: this.authHeaders(),
    });
  }

  /** getSession 404s (no session for this entity yet) fall back to starting one — that's the
   * expected first-run path, not a failure. Any other error (auth/rate-limit/Sage outage) is real
   * and must propagate rather than being swallowed into a fake "0 records" row. */
  private getOrStartSession(
    entityType: SageEntityTypeName,
    latest: Record<string, SageReconciliationSession | null>
  ): Observable<{ session?: SageReconciliationSession; sessionId?: number; matches: SageReconciliationMatch[]; confirmed?: boolean }> {
    return this.getSession(entityType).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 404) return throwError(() => err);
        // Confirmed by a first sync that stopped part-way: a new session would re-fetch every Sage
        // record and propose matches for records that are already linked.
        return latest[entityType]?.status === 'Confirmed'
          ? of({ matches: [], confirmed: true })
          : this.startReconciliation(entityType);
      })
    );
  }

  /** Bundles the status/onboarding-state/entity-session calls the overview screen needs into one
   * cached observable — whichever of the connect or overview screens asks first triggers the
   * fetch, the other gets the resolved result for free (see SageConnectComponent.runAnalysingSequence). */
  getOverviewBundle(forceRefresh = false): Observable<OverviewBundle> {
    if (!this.overviewBundle$ || forceRefresh) {
      this.overviewBundle$ = this.getStatus().pipe(
        switchMap((status) => {
          if (!status.connected) {
            return of<OverviewBundle>({
              status,
              onboardingCompleted: false,
              entities: null,
              loadError: false,
              loadErrorMessage: '',
            });
          }
          return this.getOnboardingState().pipe(
            switchMap(({ completed }) =>
              completed
                ? of<OverviewBundle>({
                    status,
                    onboardingCompleted: true,
                    entities: null,
                    loadError: false,
                    loadErrorMessage: '',
                  })
                : this.fetchOverviewEntities(status)
            ),
            // Never onboarded (first-ever connection) or a transient onboarding-state failure —
            // fall through to fetching entities, same as the wizard's existing first-run path.
            catchError(() => this.fetchOverviewEntities(status))
          );
        }),
        shareReplay(1)
      );
    }
    return this.overviewBundle$;
  }

  clearOverviewBundle(): void {
    this.overviewBundle$ = null;
  }

  private fetchOverviewEntities(status: SageConnectionStatus): Observable<OverviewBundle> {
    const routes = getEnabledEntityRoutes();
    let loadError = false;
    let loadErrorMessage = '';

    return this.getLatestSessions().pipe(
      map(({ sessions }) => sessions || {}),
      catchError(() => of<Record<string, SageReconciliationSession | null>>({})),
      switchMap((latest) =>
        forkJoin(
          routes.map((entity) =>
            this.getOrStartSession(entity.type, latest).pipe(
              catchError((err) => {
                loadError = true;
                loadErrorMessage = loadErrorMessageFor(err);
                return of(null);
              })
            )
          )
        )
      ),
      map((results): OverviewBundle => ({
        status,
        onboardingCompleted: false,
        entities: loadError
          ? null
          : results.map((result: any, i): OverviewEntityResult => ({
              slug: routes[i].slug,
              sessionId: result.session?.id ?? result.sessionId ?? null,
              matches: result.matches || [],
              confirmed: !!result.confirmed,
            })),
        loadError,
        loadErrorMessage,
      }))
    );
  }

  startReconciliation(entityType: SageEntityTypeName): Observable<ReconciliationResult> {
    return this.http.post<ReconciliationResult>(
      `${environment.apiUrl}/api/SageReconciliation/${entityType}/start`,
      null,
      { headers: this.authHeaders() }
    );
  }

  getSession(
    entityType: SageEntityTypeName
  ): Observable<{ session: SageReconciliationSession; matches: SageReconciliationMatch[] }> {
    return this.http.get<{ session: SageReconciliationSession; matches: SageReconciliationMatch[] }>(
      `${environment.apiUrl}/api/SageReconciliation/${entityType}`,
      { headers: this.authHeaders() }
    );
  }

  getLatestSessions(): Observable<{ sessions: Record<string, SageReconciliationSession | null> }> {
    return this.http.get<{ sessions: Record<string, SageReconciliationSession | null> }>(
      `${environment.apiUrl}/api/SageReconciliation/latest`,
      { headers: this.authHeaders() }
    );
  }

  getOnboardingState(): Observable<{ completed: boolean }> {
    return this.http.get<{ completed: boolean }>(
      `${environment.apiUrl}/api/SageReconciliation/onboarding`,
      { headers: this.authHeaders() }
    );
  }

  completeOnboarding(): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/api/SageReconciliation/onboarding/complete`,
      null,
      { headers: this.authHeaders() }
    );
  }

  getPendingReview(): Observable<SagePendingReviewResult> {
    return this.http.get<SagePendingReviewResult>(
      `${environment.apiUrl}/api/SageReconciliation/pending-review`,
      { headers: this.authHeaders() }
    );
  }

  updateMatchDecision(
    sessionId: number,
    matchId: number,
    body: UpdateMatchDecisionRequest
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/api/SageReconciliation/${sessionId}/matches/${matchId}`,
      body,
      { headers: this.authHeaders() }
    );
  }

  resolveUom(sessionId: number, matchId: number, body: ResolveUomMappingRequest): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/api/SageReconciliation/${sessionId}/matches/${matchId}/resolve-uom`,
      body,
      { headers: this.authHeaders() }
    );
  }

  resolveUoms(sessionId: number, matchIds: number[], purchaseUOMID: number): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/api/SageReconciliation/${sessionId}/resolve-uom`,
      { matchIds, purchaseUOMID },
      { headers: this.authHeaders() }
    );
  }

  /** Matches the apply phase could not write move to `successorSessionId`, so the entity reappears
   * in the review queue — surface `carriedForwardCount`, or that reappearance reads as a failure. */
  confirmSession(sessionId: number): Observable<SageConfirmResult> {
    return this.http.post<SageConfirmResult>(
      `${environment.apiUrl}/api/SageReconciliation/${sessionId}/confirm`,
      null,
      { headers: this.authHeaders() }
    );
  }

  /** Applies only these import rows; the rest of the review stays open. */
  confirmSelectedMatches(sessionId: number, matchIds: number[]): Observable<SageConfirmSelectedResult> {
    return this.http.post<SageConfirmSelectedResult>(
      `${environment.apiUrl}/api/SageReconciliation/${sessionId}/confirm-selected`,
      { matchIds },
      { headers: this.authHeaders() }
    );
  }

  // ---- Console (post-onboarding) ----

  getSyncStatus(): Observable<SyncStatusResponse> {
    return this.http.get<SyncStatusResponse>(
      `${environment.apiUrl}/api/sage/sync/status`,
      { headers: this.authHeaders() }
    );
  }

  triggerSync(entityType?: SageEntityTypeName): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/api/sage/sync/trigger`,
      null,
      { headers: this.authHeaders(), params: entityType ? ({ entityType } as any) : undefined }
    );
  }

  // HttpClient stringifies `undefined`/`null` filter values as the literal text "undefined"/"null"
  // rather than omitting them, so an unset filter still reaches the backend as e.g.
  // "category=undefined" and silently matches nothing. Strip those out here instead of passing a
  // plain object as `params`; arrays (e.g. category) are appended once per value.
  private toParams(filter: Record<string, any>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filter)) {
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item !== undefined && item !== null && item !== '') {
            params = params.append(key, String(item));
          }
        }
      } else {
        params = params.set(key, String(value));
      }
    }
    return params;
  }

  getSyncLogs(filter: SyncLogFilter = {}): Observable<PagedResult<SageSyncLog>> {
    return this.http.get<PagedResult<SageSyncLog>>(`${environment.apiUrl}/api/sage/sync/logs`, {
      headers: this.authHeaders(),
      params: this.toParams(filter),
    });
  }

  getSyncLogById(id: number): Observable<{ log: SageSyncLog; errors: SageSyncError[] }> {
    return this.http.get<{ log: SageSyncLog; errors: SageSyncError[] }>(
      `${environment.apiUrl}/api/sage/sync/logs/${id}`,
      { headers: this.authHeaders() }
    );
  }

  getSyncErrors(filter: SyncErrorFilter = {}): Observable<SageSyncErrorPage> {
    return this.http.get<SageSyncErrorPage>(`${environment.apiUrl}/api/sage/sync/errors`, {
      headers: this.authHeaders(),
      params: this.toParams(filter),
    });
  }

  /** Only the errors a user can clear themselves, one entry per broken record. */
  getActionableErrors(filter: ActionableErrorFilter = {}): Observable<SageActionableErrorPage> {
    return this.http.get<SageActionableErrorPage>(
      `${environment.apiUrl}/api/sage/sync/errors/actionable`,
      { headers: this.authHeaders(), params: this.toParams(filter) }
    );
  }

  /** Errors from the newest run per entity type, both inbound and outbound. */
  getLatestSessionErrors(filter: SyncErrorFilter = {}): Observable<SageLatestSessionErrorPage> {
    return this.http.get<SageLatestSessionErrorPage>(
      `${environment.apiUrl}/api/sage/sync/errors/latest`,
      { headers: this.authHeaders(), params: this.toParams(filter) }
    );
  }

  getSyncErrorById(id: number): Observable<SageSyncErrorRow> {
    return this.http.get<SageSyncErrorRow>(`${environment.apiUrl}/api/sage/sync/errors/${id}`, {
      headers: this.authHeaders(),
    });
  }

  getProvenance(entityType: SageEntityTypeName, aivorRecordId: number): Observable<SageSyncProvenance> {
    return this.http.get<SageSyncProvenance>(
      `${environment.apiUrl}/api/sage/sync/provenance/${entityType}/${aivorRecordId}`,
      { headers: this.authHeaders() }
    );
  }

  getConflicts(filter: ConflictLogFilter = {}): Observable<PagedResult<SageConflictLog>> {
    return this.http.get<PagedResult<SageConflictLog>>(`${environment.apiUrl}/api/sage/sync/conflicts`, {
      headers: this.authHeaders(),
      params: this.toParams(filter),
    });
  }

  getStockAdjustments(filter: StockAdjustmentFilter = {}): Observable<PagedResult<SageItemAdjustment>> {
    return this.http.get<PagedResult<SageItemAdjustment>>(`${environment.apiUrl}/api/sage/sync/stock-adjustments`, {
      headers: this.authHeaders(),
      params: this.toParams(filter),
    });
  }

  getStockAdjustmentReasons(): Observable<{ items: SageItemAdjustmentReason[] }> {
    return this.http.get<{ items: SageItemAdjustmentReason[] }>(
      `${environment.apiUrl}/api/sage/sync/stock-adjustments/reasons`,
      { headers: this.authHeaders() }
    );
  }

  releaseStockAdjustment(
    id: number,
    reasonId: number
  ): Observable<{ id: number; status: string; delta: number; direction: number }> {
    return this.http.post<{ id: number; status: string; delta: number; direction: number }>(
      `${environment.apiUrl}/api/sage/sync/stock-adjustments/${id}/release`,
      { reasonId },
      { headers: this.authHeaders() }
    );
  }

  cancelStockAdjustment(id: number): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/api/sage/sync/stock-adjustments/${id}/cancel`,
      null,
      { headers: this.authHeaders() }
    );
  }

  /** Finished-good items enriched with bundle info and current sync target, for the Item sync
   * targets tab — the items carry their syncTargetType already. */
  getFinishedGoodsSyncTargets(): Observable<any[]> {
    return this.getBundleCandidates(this.authHeaders());
  }

  /** Best-effort AIVOR record list for the candidate picker — reuses existing list endpoints per
   * entity type. Null when the entity has no list, so callers don't mistake it for an empty one. */
  getAivorRecords(entityType: SageEntityTypeName): Observable<any[] | null> {
    const headers = this.authHeaders();
    switch (entityType) {
      case 'Supplier':
        return this.http.get<any[]>(`${environment.apiUrl}/api/Suppliers/GetAll`, { headers });
      case 'Item':
        return this.http.get<any[]>(`${environment.apiUrl}/api/Items/GetAll`, { headers });
      case 'ItemCategory':
        return this.http.get<any[]>(`${environment.apiUrl}/api/Category/GetAll`, { headers });
      case 'ItemBundle':
        return this.getBundleCandidates(headers);
      case 'SupplierCategory':
        return this.http.get<any[]>(`${environment.apiUrl}/api/SupplierCategory/GetAll`, { headers });
      default:
        return of(null);
    }
  }

  /** Finished-good items (those with a bill of materials valid today) enriched with bundle info for the
   * Item Bundles mapping table — reuses the existing Items/BOMHeaders/BOMComponents list endpoints,
   * no dedicated bundle read endpoint exists. Services and buy-outs have a BOM that only points at
   * themselves; they go to Sage as ordinary items, so they're left out, as are variable items,
   * which stay out of Sage. */
  private getBundleCandidates(headers: HttpHeaders): Observable<any[]> {
    return forkJoin({
      items: this.http.get<any[]>(`${environment.apiUrl}/api/Items/GetAll`, { headers }),
      bomHeaders: this.http.get<any[]>(`${environment.apiUrl}/api/BOMHeaders/GetAll`, { headers }),
      bomComponents: this.http.get<any[]>(`${environment.apiUrl}/api/BOMComponents/GetAll`, { headers }),
    }).pipe(
      map(({ items, bomHeaders, bomComponents }) => {
        const today = new Date();
        return items
          .filter((i) => currentBomHeader(bomHeaders, i.itemID ?? i.ItemID, today))
          .filter((i) => !sellsAsIs({ itemTypeID: i.itemTypeID ?? i.ItemTypeID, isBuyout: i.isBuyout ?? i.IsBuyout }))
          .filter((i) => !isKeptOutOfSage(i))
          .map((i) => ({
            ...i,
            bundleInfo: computeBundleInfo(i.itemID ?? i.ItemID, bomHeaders, bomComponents, items, today),
          }));
      })
    );
  }

  /** Persists the Bundle/Standalone target on the AIVOR finished-good record — re-evaluated by the
   * outbound sync on every run, so this is a field on Items, not on the mapping row (SAGE.BDL.012). */
  updateSyncTarget(itemId: number, target: 'Bundle' | 'Standalone'): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/api/Items/${itemId}/sync-target`,
      { syncTargetType: target },
      { headers: this.authHeaders() }
    );
  }

  /** All or nothing. A null group puts the items back on the default sales group. */
  setItemsSalesGroup(itemIds: number[], salesReportingGroupId: number | null): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/api/Items/sales-reporting-group`,
      { itemIds, salesReportingGroupId },
      { headers: this.authHeaders() }
    );
  }

  /** Only these two fields change. A code another item uses is a 409 naming that item. */
  updateItemCodeAndName(itemId: number, itemCode: string, itemName: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/api/Items/${itemId}/code-and-name`,
      { itemCode, itemName },
      { headers: this.authHeaders() }
    );
  }

  /** Per-component dependency list for a bundle's active BOM — drives the "Component dependency"
   * expandable table in the Item Bundles config panel (SAGE.BDL.007/010). */
  getComponentsWithSageInfo(finishedGoodItemId: number): Observable<BOMComponentsWithSageInfoResult> {
    return this.http.get<BOMComponentsWithSageInfoResult>(
      `${environment.apiUrl}/api/BOMComponents/GetWithSageInfoByItem`,
      { headers: this.authHeaders(), params: { finishedGoodItemId } as any }
    );
  }

  getLinks(type: FixMatchType): Observable<SageLinkRow[]> {
    return this.http.get<SageLinkRow[]>(`${environment.apiUrl}/api/sage/links/${type}`, {
      headers: this.authHeaders(),
    });
  }

  moveLink(type: FixMatchType, body: SageLinkMoveRequest): Observable<SageLinkMoveDetails> {
    return this.http.post<SageLinkMoveDetails>(`${environment.apiUrl}/api/sage/links/${type}/move`, body, {
      headers: this.authHeaders(),
    });
  }

  addLinkAsNew(type: FixMatchType, body: SageLinkAddRequest): Observable<SageLinkAddDetails> {
    return this.http.post<SageLinkAddDetails>(`${environment.apiUrl}/api/sage/links/${type}/add-as-new`, body, {
      headers: this.authHeaders(),
    });
  }

  /** Options for the UOM-resolution picker on Item rows blocked with a `NeedsUomMapping` decision. */
  getUnitsOfMeasure(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/UnitOfMeasure/GetAll`, {
      headers: this.authHeaders(),
    });
  }

  /** Local cache of Sage GL reporting groups, shown read-only alongside Item rows. */
  getItemReportGroups(): Observable<ItemReportGroup[]> {
    return this.http.get<ItemReportGroup[]>(`${environment.apiUrl}/api/ItemReportGroups`, {
      headers: this.authHeaders(),
    });
  }

  /** Pulls the latest GL reporting groups from Sage into the local cache — fired once when a fresh connection completes. */
  refreshItemReportGroups(): Observable<{ count: number }> {
    return this.http.post<{ count: number }>(
      `${environment.apiUrl}/api/ItemReportGroups/refresh`,
      null,
      { headers: this.authHeaders() }
    );
  }

  getTenantSageSettings(): Observable<TenantSageSettings> {
    return this.http.get<TenantSageSettings>(`${environment.apiUrl}/api/TenantSageSettings`, {
      headers: this.authHeaders(),
    });
  }

  setDefaultSalesReportingGroup(itemReportGroupId: number): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/api/TenantSageSettings/default-sales-reporting-group`,
      { itemReportGroupId },
      { headers: this.authHeaders() }
    );
  }
}
